import { useEffect, useRef } from "react";
import { Dropdown } from "react-bootstrap";
import moment from "moment";
import Style from "./Notification.module.scss";
import useNotification from "./useNotification";

const formatTime = (value) => {
  if (!value) return "";
  const m = moment(value);
  return m.isValid() ? m.fromNow() : "";
};

const getTitle = (item) => item?.message || "Bulk upload notification";

const NotificationDropdown = () => {
  const {
    notifications,
    unreadCount,
    displayCount,
    hasUnread,
    isInitialLoading,
    isFetchingMore,
    hasMore,
    loadMore,
    listError,
    isOpen,
    toggleDropdown,
    refetchList,
    handleNotificationClick,
    handleMarkAllAsRead,
  } = useNotification();

  const listRef = useRef(null);
  const sentinelRef = useRef(null);

  const showSkeleton = isInitialLoading;
  const showError = !showSkeleton && listError && notifications.length === 0;
  const showEmpty =
    !showSkeleton && !listError && notifications.length === 0;
  const showList = !showSkeleton && notifications.length > 0;

  useEffect(() => {
    if (!isOpen || !showList || !hasMore) return;
    const sentinel = sentinelRef.current;
    const root = listRef.current;
    if (!sentinel || !root) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) loadMore();
      },
      { root, rootMargin: "80px" },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [isOpen, showList, hasMore, loadMore]);

  return (
    <>
      <div className={Style.wrap}>
        <Dropdown
          show={isOpen}
          onToggle={toggleDropdown}
          className={`pro-d-flex pro-items-center ${Style.root}`}
          align="end"
        >
          <Dropdown.Toggle
            id="notification-dropdown"
            aria-label="Notifications"
            className={Style.toggle}
          >
            <span className={Style.icon_wrap}>
              <span
                className={`material-symbols-outlined ${Style.bell}`}
                aria-hidden="true"
              >
                notifications
              </span>
              {hasUnread && (
                <span
                  className={Style.badge}
                  aria-label={`${unreadCount} unread`}
                >
                  {displayCount}
                </span>
              )}
            </span>
          </Dropdown.Toggle>

          <Dropdown.Menu className={Style.dropdown}>
            <div className={Style.header}>
              <h4 className={Style.heading}>Notifications</h4>
              {unreadCount > 0 && (
                <button
                  type="button"
                  className={Style.markAll}
                  onClick={handleMarkAllAsRead}
                >
                  Mark all as read
                </button>
              )}
            </div>

            <div className={Style.list} ref={listRef}>
              {showSkeleton && (
                <div className={Style.skeletonWrap}>
                  {[0, 1, 2].map((i) => (
                    <div key={i} className={Style.skeleton} />
                  ))}
                </div>
              )}

              {showError && (
                <div className={Style.state}>
                  <p>Could not load notifications.</p>
                  <button
                    type="button"
                    className={Style.retry}
                    onClick={refetchList}
                  >
                    Retry
                  </button>
                </div>
              )}

              {showEmpty && (
                <div className={Style.state}>
                  <span
                    className={`material-symbols-outlined ${Style.emptyIcon}`}
                    aria-hidden="true"
                  >
                    notifications_off
                  </span>
                  <p>No new notifications</p>
                </div>
              )}

              {showList &&
                notifications.map((n) => (
                  <div
                    key={n._id}
                    className={`${Style.row} ${!n.is_read ? Style.unread : ""}`}
                  >
                    <button
                      type="button"
                      className={Style.rowMain}
                      onClick={() => handleNotificationClick(n)}
                    >
                      <span className={Style.rowDot} aria-hidden="true" />
                      <span className={Style.rowContent}>
                        <span className={Style.title}>{getTitle(n)}</span>
                        {n?.created_at && (
                          <span className={Style.time}>
                            {formatTime(n.created_at)}
                          </span>
                        )}
                      </span>
                    </button>

                    {n.type === 1 && n.download_url && (
                      <a
                        href={n.download_url}
                        download
                        target="_blank"
                        rel="noopener noreferrer"
                        className={Style.download}
                        aria-label="Download export file"
                        title="Download"
                      >
                        <span
                          className="material-symbols-outlined"
                          aria-hidden="true"
                        >
                          download
                        </span>
                      </a>
                    )}
                  </div>
                ))}

              {showList && hasMore && (
                <div ref={sentinelRef} className={Style.sentinel}>
                  {isFetchingMore && (
                    <span className={Style.loadingMore}>Loading more…</span>
                  )}
                </div>
              )}

              {showList && !hasMore && notifications.length > 0 && (
                <div className={Style.endOfList}>You're all caught up</div>
              )}
            </div>
          </Dropdown.Menu>
        </Dropdown>
      </div>
    </>
  );
};

export default NotificationDropdown;
